import { useEffect, useState } from 'react';
import { optionsForSelects } from 'pages/Notifications/types/NotificationsTypes';
import { useParams } from 'react-router-dom';
import { RouteParams } from 'pages/Settings/types';
import { SelectDataItem } from '../ui/DynamicSelects';

interface useDynamicSelectsProps {
	initialValue?: any;
	options?: optionsForSelects[];
	onChange?: (value: any) => void;
	tagNamesWithValues?: string[];
}

export const useDynamicSelects = (props: useDynamicSelectsProps) => {
	const { initialValue, options, onChange, tagNamesWithValues } = props;
	const { lang, notifyRuleId } = useParams<RouteParams>();

	const [selectData, setSelectData] = useState<SelectDataItem[]>([]);
	const [dataIsLoading, setDataIsLoading] = useState(true);
	const [lastUpdatedRuleId, setLastUpdatedRuleId] = useState<string | null>(null);

	const tagOptions = tagNamesWithValues?.map((tagName, index) => ({ label: tagName, value: index.toString() })) || [];

	// Обрабатываем флаг dataIsLoading
	useEffect(() => {
		if (initialValue?.length > 0 && (options?.length ?? 0) > 0 && (tagNamesWithValues?.length ?? 0) > 0) {
			setDataIsLoading(false);
		}
	}, [initialValue, options, tagNamesWithValues]);

	// Устанавливаем данные из initialValue в selectData
	useEffect(() => {
		if (!dataIsLoading) {
			setSelectData(
				initialValue.map((val: any) => {
					const leftSelected = options?.find((el) => el.value === val.value);
					const rightSelected = tagOptions.find((el) => el.label === val.label);

					return {
						leftSelected: leftSelected || { label: '', value: '' },
						rightSelected: rightSelected || { label: '', value: '' },
						leftOptions: options,
						rightOptions: tagOptions,
					};
				}),
			);
		}
	}, [dataIsLoading]);

	// Перезаписываем данные из initialValue в selectData, если юзер переключился между правилами
	useEffect(() => {
		if (notifyRuleId && lastUpdatedRuleId !== notifyRuleId) {
			setSelectData(
				initialValue.map((val: any) => {
					const leftSelected = options?.find((el) => el.value === val.value);
					const rightSelected = tagOptions.find((el) => el.label === val.label);

					return {
						leftSelected: leftSelected || { label: '', value: '' },
						rightSelected: rightSelected || { label: '', value: '' },
						leftOptions: options,
						rightOptions: tagOptions,
					};
				}),
			);
			setLastUpdatedRuleId(notifyRuleId);
		}
	}, [notifyRuleId, initialValue, options, tagOptions]);

	const handleAdd = () => {
		setSelectData([
			...selectData,
			{
				leftSelected: { label: '', value: '' },
				rightSelected: { label: '', value: '' },
				leftOptions: options,
				rightOptions: tagOptions,
			},
		]);
	};

	const handleRemove = (index: number) => {
		const newSelects = [...selectData];
		newSelects.splice(index, 1);
		setSelectData(newSelects);

		if (onChange) {
			onChange(newSelects);
		}
	};

	const handleSelectChange = (value: any, index: number, isLeft: boolean) => {
		setSelectData((prevSelectData) => {
			const newSelectData = [...prevSelectData];
			if (isLeft) {
				newSelectData[index].leftSelected = value;
			} else {
				newSelectData[index].rightSelected = value;
			}
			return newSelectData;
		});

		if (onChange) {
			onChange(selectData);
		}
	};

	const dynamicSelectsData = { lang, selectData };

	const dynamicSelectsMathods = { handleAdd, handleRemove, handleSelectChange };

	return { dynamicSelectsData, dynamicSelectsMathods };
};
